import { useEffect, useRef, useState } from 'react'
import { isAtomicSameChain } from '../../utils/sameChainGate'
import { X, Check, ExternalLink, ChevronDown, ChevronRight, Trash2, History, Wallet, Inbox } from 'lucide-react'
import { ChainIcon } from '@/components/ChainIcon'
import { TokenIcon } from '@/components/icons/TokenIcons'
import { TOKEN_PICKER_LIST } from '@/config/tokens'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useViewportSize } from '@/hooks/useViewportSize'
import { FONT_HEADING, FONT_BODY, FONT_MONO } from '@/config/theme'
import { cancelReasonText, type DexCancelControl } from '@/utils/cancelEligibility'
import { isExplorableTxHash } from '@/utils/format'
import type { Chain } from '@/types'

export interface ActivityTransfer {
  id: string
  amount: string
  sourceToken: string
  destToken: string
  sourceChain: Chain
  destinationChain: Chain
  status: string
  burnTxHash?: string | null
  mintTxHash?: string | null
  explorerUrl?: string | null
  timestamp?: number
  /** Distinguish bridge transfers from reward claims */
  type?: 'bridge' | 'claim'
  /**
   * #614 — the rail the row settled on (`near_1click` | `debridge_same` | `jupiter` | `cctp` | …).
   * Threaded explicitly, never derived: a same-chain `near_1click` row (Tron TRX↔USDT) has a separate
   * delivery tx and must render Sent + Received, not the single atomic "Swap" leg. ABSENT ⇒ ATOMIC per
   * the G6 operator decision (correct for every legacy row); the fail-closed edge is a row KNOWN to be
   * `near_1click`. A producer that omits the provider on a same-chain 1Click row therefore reopens the
   * deposit-as-delivery bug — every producer in BridgePage's `activityTransfers` mapper sets it.
   */
  provider?: string | null
  /**
   * #261 F9 — the failure message persisted with the row, and the phase it died in
   * (`StoredDexOrder.{error,failedStep}`). Both are threaded through the parent's `activityTransfers`
   * mapper purely so the ProgressPanel this row opens can red the step that actually failed. Neither
   * is rendered by the drawer itself, and both are absent on rows from every other source — notably
   * on every `/bridge/history` row, whose payload has neither field.
   *
   * `failedStep` is the load-bearing one: it selects `makeDexSteps`' per-step path, and its absence
   * degrades to the all-red fallback. `error` no longer participates in that decision — the fallback
   * used to be keyed on this string being truthy, which rendered a message-less failed row as three
   * green checkmarks; it is keyed on the phase now (see `makeDexSteps` in utils/steps.ts).
   */
  error?: string | null
  failedStep?: string | null
  /**
   * The wallet-signed cancel control for this row, computed by the parent (visibility + enabled + reason).
   * When `visible`, the drawer ALWAYS renders the cancel button — enabled (opens the review) or disabled
   * (with a tooltip reason) — so the manual escape hatch is discoverable. Only owned, 'Created' deBridge
   * cross-chain orders are enabled, mirroring the backend so an enabled click never hits "not cancellable".
   */
  cancelControl?: DexCancelControl
}

/**
 * #261 L6 — which badge stands for ONE leg of a row.
 *
 * The rule (operator, 2026-08-03): a **stablecoin** shows its TOKEN mark, because USDC-vs-USDT is
 * that leg's whole identity and the chain is the shared, uninteresting part; a **native** shows
 * its CHAIN mark, because the native asset IS the network.
 *
 * `TokenIcon` cannot be used unconditionally to get that second half, even though it does map
 * natives to a chain logo internally: its map is symbol → CANONICAL chain (see `NATIVE_TOKEN_LOGO`
 * in `components/icons/TokenIcons.tsx`, `ETH: 'ethereum'`), so a **Base** ETH leg would draw the
 * **Ethereum** mark. The leg's own chain is the correct badge, which is why the branch below is on
 * the leg's `chain`, not on the token's idea of one.
 *
 * An unknown symbol falls to `ChainIcon` rather than `TokenIcon`'s coloured-initial placeholder: a
 * real network badge beats a grey "U" circle, and this drawer renders `/bridge/history` rows whose
 * token strings this build has never seen.
 */
type LegMark =
  | { kind: 'token'; symbol: string; color?: string }
  | { kind: 'chain'; chain: Chain }

function legMark(token: string | null | undefined, chain: Chain): LegMark {
  const meta = token ? TOKEN_PICKER_LIST.find(t => t.symbol === token) : undefined
  if (!meta || meta.isNative) return { kind: 'chain', chain }
  return { kind: 'token', symbol: meta.symbol, color: meta.color }
}

/** Identity of a mark, so "would these two draw the same badge?" is one comparison. */
function markKey(m: LegMark): string {
  return m.kind === 'token' ? `token:${m.symbol}` : `chain:${m.chain}`
}

/**
 * #261 L6 — the from→to PAIR, which is a row-level decision and not two independent ones.
 *
 * Applying the per-leg rule alone is not enough, and getting this wrong once already turned one
 * bug into a worse one. The rows originally drew `ChainIcon(source) → ChainIcon(dest)`, which
 * collapsed to the same badge twice on a same-chain swap (Base USDC → Base ETH). Switching to
 * per-leg token marks fixed that and broke the **dominant** case in its place: a cross-chain
 * USDC→USDC bridge — every CCTP transfer, plus every legacy row, whose token columns default to
 * USDC on both legs (`routes/hybrid.ts`) — has a stablecoin on both sides, so both legs resolved
 * to the same USDC mark.
 *
 * That is worse than a cosmetic tie, because these icons are the row's ONLY chain signal: the
 * summary text appends `→ {destToken}` just when the tokens DIFFER, and the expanded detail omits
 * the chain deliberately, citing this icon pair as the thing that already carries it. A
 * base→arbitrum and an ethereum→base USDC bridge would have rendered as identical rows.
 *
 * So: take the per-leg marks, and if they would draw the SAME badge, fall back to the chains,
 * which is the axis that still distinguishes the legs.
 *
 * KNOWN RESIDUAL, stated rather than papered over — this does NOT make "never two identical
 * badges" unconditional. Two legs can collide on the SAME chain when both fall to the chain mark:
 * a native paired with an OFF-LIST symbol (`legMark` sends anything absent from `TOKEN_PICKER_LIST`
 * here), e.g. a same-chain ETH→WETH row from `/bridge/history`. The fallback then has no differing
 * axis to reach for and the badges stay identical.
 *
 * Left alone deliberately. Resolving it means tie-breaking on token marks, and `TokenIcon` renders
 * natives by symbol → CANONICAL chain — so it would put the ETHEREUM badge on a Base row to avoid a
 * duplicate one. A duplicate badge is a missing distinction; a wrong network badge is a false
 * statement about where the user's money is, and that is the worse of the two. No live producer
 * emits an off-list symbol (every picker path uses `TOKEN_PICKER_LIST`, and `routes/hybrid.ts`
 * defaults null token columns to USDC), and when it does fire the row renders exactly what it
 * rendered before #261 L6 — chain → chain — with the differing tokens still named in the row text.
 */
function hopMarks(
  sourceToken: string | null | undefined, sourceChain: Chain,
  destToken: string | null | undefined, destChain: Chain,
): [LegMark, LegMark] {
  const from = legMark(sourceToken, sourceChain)
  const to = legMark(destToken, destChain)
  if (markKey(from) === markKey(to)) {
    return [{ kind: 'chain', chain: sourceChain }, { kind: 'chain', chain: destChain }]
  }
  return [from, to]
}

function LegIcon({ mark, size = 18 }: { mark: LegMark; size?: number }) {
  return mark.kind === 'chain'
    ? <ChainIcon chain={mark.chain} size={size} />
    : <TokenIcon token={{ symbol: mark.symbol, color: mark.color }} size={size} />
}

/** Per-order cancel progress, keyed by transfer id, supplied by the parent's useCancelOrder hook. */
export interface CancelUiState {
  status: 'idle' | 'signing' | 'submitting' | 'success' | 'error'
  error?: string
}

interface ActivityDrawerProps {
  open: boolean
  onClose: () => void
  transfers: ActivityTransfer[]
  activeTransfer?: ActivityTransfer | null
  onDismiss: (id: string) => void
  onDismissAll: () => void
  getExplorerUrl?: (hash: string, chain: Chain) => string
  inProgressCount?: number
  walletConnected?: boolean
  onConnectWallet?: () => void
  /** Request a wallet-signed cancel for the given transfer id (opens the review; see ActivityTransfer.cancelControl). */
  onCancelOrder?: (id: string) => void
  /** Per-order cancel progress keyed by transfer id. */
  cancelStates?: Record<string, CancelUiState>
  /** Open the full ProgressPanel for a (bridge) transfer. When provided, clicking a bridge row body opens its
   *  progress view; the chevron still expands the inline detail (tx links / cancel / dismiss). */
  onSelectTransfer?: (t: ActivityTransfer) => void
}

export function ActivityDrawer({
  open,
  onClose,
  transfers,
  activeTransfer,
  onDismiss,
  onDismissAll,
  getExplorerUrl,
  inProgressCount = 0,
  walletConnected = false,
  onConnectWallet,
  onCancelOrder,
  cancelStates,
  onSelectTransfer,
}: ActivityDrawerProps) {
  const c = useThemeColors()
  const viewportSize = useViewportSize()
  const isMobile = viewportSize === 'small' || viewportSize === 'mobile'
  const overlayRef = useRef<HTMLDivElement>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [confirmClear, setConfirmClear] = useState(false)

  // Lock scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
      setExpandedId(null)
      setConfirmClear(false)
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  // Escape key
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  const IN_PROGRESS_STATUSES = ['in_progress', 'pending', 'burn_submitted', 'burn_success', 'attestation_pending', 'attestation_received', 'mint_submitted', 'signed', 'confirmed', 'polling']
  const COMPLETE_STATUSES = ['complete', 'mint_success', 'recovered']
  const FAILED_STATUSES = ['failed', 'error']

  const statusColor = (status: string) => {
    if (COMPLETE_STATUSES.includes(status)) return c.gr
    if (FAILED_STATUSES.includes(status)) return c.eC
    if (IN_PROGRESS_STATUSES.includes(status)) return c.ac
    return c.wC
  }

  const statusBg = (status: string) => {
    if (COMPLETE_STATUSES.includes(status)) return c.oB
    if (FAILED_STATUSES.includes(status)) return c.eB
    if (IN_PROGRESS_STATUSES.includes(status)) return c.aB
    return c.wB
  }

  const statusLabel = (status: string, type?: string) => {
    if (type === 'claim') return 'Claimed'
    if (COMPLETE_STATUSES.includes(status)) return 'Delivered'
    if (FAILED_STATUSES.includes(status)) return 'Failed'
    if (IN_PROGRESS_STATUSES.includes(status)) return 'In progress'
    return status.replace(/_/g, ' ')
  }

  const StatusIcon = ({ status }: { status: string }) => {
    if (COMPLETE_STATUSES.includes(status)) {
      return <Check size={10} strokeWidth={3} />
    }
    if (FAILED_STATUSES.includes(status)) {
      return <X size={10} strokeWidth={3} />
    }
    if (IN_PROGRESS_STATUSES.includes(status)) {
      return (
        <span style={{
          width: 10, height: 10,
          border: '1.5px solid currentColor', borderTopColor: 'transparent',
          borderRadius: '50%', display: 'inline-block',
          animation: 'spin 0.8s linear infinite',
        }} />
      )
    }
    return null
  }

  /**
   * Wallet-signed cancel button — rendered next to Dismiss on history rows AND on the pinned active row,
   * for any transfer whose cancelControl.visible is true. ALWAYS shown when visible (so the escape hatch is
   * discoverable); enabled only when cancelControl.enabled (opens the review via onCancelOrder), otherwise
   * disabled with a tooltip reason. While a cancel is signing/submitting it shows a spinner + "Cancelling…".
   */
  const CancelControlButton = ({ tx }: { tx: ActivityTransfer }) => {
    if (!tx.cancelControl?.visible || !onCancelOrder) return null
    const cs = cancelStates?.[tx.id]
    const inFlight = cs?.status === 'signing' || cs?.status === 'submitting'
    const enabled = tx.cancelControl.enabled && !inFlight
    const label = cs?.status === 'signing' ? 'Waiting for signature…'
      : cs?.status === 'submitting' ? 'Cancelling…'
      : 'Cancel'
    const title = inFlight ? 'Cancellation in progress…' : cancelReasonText(tx.cancelControl.reason)
    return (
      <button
        onClick={() => { if (enabled) onCancelOrder(tx.id) }}
        disabled={!enabled}
        title={title}
        aria-label={enabled ? 'Cancel order' : `Cancel unavailable: ${title ?? ''}`}
        style={{
          padding: '4px 10px', fontSize: 11,
          background: enabled ? c.eB : 'transparent',
          border: `1px solid ${enabled ? c.eC : c.dv}`,
          color: enabled ? c.eC : c.mu,
          borderRadius: 6, cursor: enabled ? 'pointer' : 'default',
          opacity: enabled ? 1 : 0.6,
          fontFamily: FONT_BODY,
          display: 'inline-flex', alignItems: 'center', gap: 6,
          WebkitAppearance: 'none', appearance: 'none' as React.CSSProperties['appearance'],
        }}
      >
        {inFlight && (
          <span style={{
            width: 10, height: 10,
            border: '1.5px solid currentColor', borderTopColor: 'transparent',
            borderRadius: '50%', display: 'inline-block',
            animation: 'spin 0.8s linear infinite',
          }} />
        )}
        {label}
      </button>
    )
  }

  const drawerWidth = isMobile ? '100%' : 380

  /** Shared row style — used for both active and history rows */
  const rowStyle: React.CSSProperties = {
    width: '100%', display: 'flex', alignItems: 'center',
    gap: 10, padding: '12px 14px',
    background: 'transparent', border: 'none',
    cursor: 'pointer', fontFamily: FONT_BODY,
  }

  // Fixed-width hash abbreviation (first 6 + last 4) so the right side is constant-width regardless of hash.
  const abbrevHash = (h: string) => `${h.slice(0, 6)}…${h.slice(-4)}`

  // One tx leg as a single nowrap line: label pinned left, [abbrev ↗] pinned right (one clickable link). The
  // chain is NOT shown (redundant with the card's source→dest, and the long offender on a narrow drawer) — it's
  // only used to pick the explorer. `pendingLabel` (muted, no link) covers the not-yet-landed dest leg.
  const legRow = (label: string, hash: string | null | undefined, href: string | undefined, pendingLabel?: string) => (
    <div key={label} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      whiteSpace: 'nowrap', gap: 8, fontSize: 12, color: c.mu,
    }}>
      <span>{label}</span>
      {hash && href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" title={hash} aria-label={`${label} transaction ${hash}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: c.ac, textDecoration: 'none', fontFamily: FONT_MONO }}>
          {abbrevHash(hash)}<ExternalLink size={10} style={{ flexShrink: 0 }} />
        </a>
      ) : pendingLabel ? (
        <span style={{ color: c.mu, fontStyle: 'italic' }}>{pendingLabel}</span>
      ) : null}
    </div>
  )

  // Sent / Received for cross-chain (keyed off burn/mint, unified across CCTP + DEX), Swap for same-chain
  // (one atomic tx), single "Tx" for claims. A failed/cancelled transfer never delivers → no pending Received.
  const renderLegs = (t: ActivityTransfer) => {
    if (t.type === 'claim') return t.burnTxHash ? legRow('Claim', t.burnTxHash, t.explorerUrl ?? undefined) : null
    // Terminal (success or failure) but no dest tx → neutral "—"; still in-flight → "Delivering…".
    const terminal = ['mint_success', 'complete', 'recovered', 'failed', 'error', 'mint_failed', 'cancelled', 'refunded'].includes(t.status)
    if (isAtomicSameChain(t.sourceChain, t.destinationChain, t.provider)) {
      return t.burnTxHash ? legRow('Swap', t.burnTxHash, getExplorerUrl?.(t.burnTxHash, t.sourceChain)) : null
    }
    return (
      <>
        {t.burnTxHash && legRow('Sent', t.burnTxHash, getExplorerUrl?.(t.burnTxHash, t.sourceChain))}
        {legRow('Received',
          isExplorableTxHash(t.mintTxHash) ? t.mintTxHash : undefined,
          isExplorableTxHash(t.mintTxHash) ? getExplorerUrl?.(t.mintTxHash, t.destinationChain) : undefined,
          isExplorableTxHash(t.mintTxHash) ? undefined : (terminal ? '—' : 'Delivering…'))}
      </>
    )
  }

  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        onClick={(e) => { if (e.target === overlayRef.current) onClose() }}
        style={{
          position: 'fixed', inset: 0, zIndex: 50,
          background: c.mo,
          animation: 'fadeIn 0.2s ease-out',
        }}
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-label="Activity"
        style={{
          position: 'fixed',
          top: isMobile ? 'auto' : 0,
          right: isMobile ? 0 : 0,
          bottom: 0,
          left: isMobile ? 0 : 'auto',
          width: drawerWidth,
          height: isMobile ? '85dvh' : '100dvh',
          background: c.card,
          borderLeft: isMobile ? 'none' : `1px solid ${c.cb}`,
          borderTopLeftRadius: isMobile ? 20 : 0,
          borderTopRightRadius: isMobile ? 20 : 0,
          zIndex: 51,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          animation: isMobile ? 'slideUp 0.3s ease-out' : 'slideInRight 0.3s ease-out',
          fontFamily: FONT_BODY,
        }}
      >
        {/* Handle bar (mobile) */}
        {isMobile && (
          <div style={{
            display: 'flex', justifyContent: 'center', padding: '8px 0 4px',
          }}>
            <div style={{
              width: 36, height: 4, borderRadius: 2,
              background: c.dv,
            }} />
          </div>
        )}

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 20px',
          borderBottom: `1px solid ${c.dv}`,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <History size={20} style={{ color: c.ac }} />
            <span style={{
              fontSize: 18, fontWeight: 600, fontFamily: FONT_HEADING,
              color: c.tx,
            }}>
              Activity
            </span>
            {inProgressCount > 0 && (
              <span style={{
                background: c.aB, color: c.ac,
                fontSize: 12, fontWeight: 600, fontFamily: FONT_BODY,
                borderRadius: 999, padding: '2px 8px',
              }}>
                {inProgressCount} in progress
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {transfers.length > 0 && (
              <button
                onClick={() => {
                  if (confirmClear) {
                    onDismissAll()
                    setConfirmClear(false)
                  } else {
                    setConfirmClear(true)
                    setTimeout(() => setConfirmClear(false), 3000)
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '6px 10px', borderRadius: 8,
                  background: confirmClear ? c.eB : 'transparent',
                  border: `1px solid ${confirmClear ? c.eC : c.dv}`,
                  color: confirmClear ? c.eC : c.mu,
                  fontSize: 12, fontWeight: 500, cursor: 'pointer',
                  transition: 'all 0.15s', fontFamily: FONT_BODY,
                  WebkitAppearance: 'none', appearance: 'none' as any,
                }}
              >
                <Trash2 size={12} />
                {confirmClear ? 'Confirm clear' : 'Clear all'}
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close activity"
              style={{
                background: 'none', border: 'none',
                color: c.mu, cursor: 'pointer', padding: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                WebkitAppearance: 'none', appearance: 'none' as any,
              }}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Transfer list */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {transfers.length === 0 && !activeTransfer && (
            !walletConnected ? (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 14, padding: '48px 20px', textAlign: 'center',
              }}>
                <div style={{
                  width: 60, height: 60, borderRadius: '50%',
                  background: c.iB, border: `1px solid ${c.dv}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Wallet size={26} style={{ color: c.mu }} />
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: c.tx, fontFamily: FONT_HEADING, marginBottom: 4 }}>
                    No wallet connected
                  </div>
                  <div style={{ fontSize: 13, color: c.mu, lineHeight: 1.5, maxWidth: 260 }}>
                    Connect your wallet to see your transfer history.
                  </div>
                </div>
                {onConnectWallet && (
                  <button
                    onClick={() => { onClose(); onConnectWallet() }}
                    style={{
                      background: 'linear-gradient(135deg, #00AAFF, #00CC66)',
                      color: '#fff', border: 'none',
                      padding: '10px 20px', borderRadius: 12, cursor: 'pointer',
                      fontSize: 14, fontWeight: 600, fontFamily: FONT_HEADING,
                      WebkitAppearance: 'none', appearance: 'none' as any,
                    }}
                  >
                    Connect wallet
                  </button>
                )}
              </div>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 14, padding: '48px 20px', textAlign: 'center',
              }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: c.iB, border: `1px solid ${c.dv}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Inbox size={26} style={{ color: c.mu }} />
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: c.tx, fontFamily: FONT_HEADING, marginBottom: 4 }}>
                    No transfers yet
                  </div>
                  <div style={{ fontSize: 13, color: c.mu, lineHeight: 1.5, maxWidth: 260 }}>
                    Your transfers will show up here. Everything stays private to this wallet.
                  </div>
                </div>
              </div>
            )
          )}

          {/* Pinned active transfer — rendered directly from prop, bypasses memo chain */}
          {activeTransfer && (
            <div style={{
              background: c.aB, border: `1px solid ${c.ac}33`,
              borderRadius: 14,
            }}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setExpandedId(expandedId === activeTransfer.id ? null : activeTransfer.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedId(expandedId === activeTransfer.id ? null : activeTransfer.id) } }}
                style={rowStyle}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                  {(() => {
                    const [from, to] = hopMarks(
                      activeTransfer.sourceToken, activeTransfer.sourceChain,
                      activeTransfer.destToken, activeTransfer.destinationChain,
                    )
                    return (
                      <>
                        <LegIcon mark={from} size={18} />
                        <span style={{ fontSize: 10, color: c.mu }}>→</span>
                        <LegIcon mark={to} size={18} />
                      </>
                    )
                  })()}
                </div>
                <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: c.tx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {activeTransfer.amount} {activeTransfer.sourceToken}
                    {activeTransfer.sourceToken !== activeTransfer.destToken && ` → ${activeTransfer.destToken}`}
                  </div>
                  <div style={{ fontSize: 11, color: c.mu, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Just now</div>
                </div>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 11, padding: '2px 8px', borderRadius: 6,
                  background: c.aB, color: c.ac,
                  fontWeight: 600, whiteSpace: 'nowrap',
                }}>
                  <span style={{
                    width: 10, height: 10,
                    border: '1.5px solid currentColor', borderTopColor: 'transparent',
                    borderRadius: '50%', display: 'inline-block',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                  In progress
                </span>
                {expandedId === activeTransfer.id
                  ? <ChevronDown size={14} color={c.mu} />
                  : <ChevronRight size={14} color={c.mu} />}
              </div>
              {expandedId === activeTransfer.id && (
                <div style={{
                  padding: '0 14px 12px',
                  display: 'flex', flexDirection: 'column', gap: 6,
                  animation: 'fadeIn 0.15s ease-out',
                }}>
                  {renderLegs(activeTransfer)}
                  {/* Cancel control on the pinned active row — this is the in-flight order, exactly the
                      window users want to cancel (deBridge 'Created', waiting for a fill). */}
                  {activeTransfer.cancelControl?.visible && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                      <CancelControlButton tx={activeTransfer} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {transfers.map((tx) => {
            // Skip the active transfer from the main list to avoid duplicates
            if (activeTransfer && tx.burnTxHash && tx.burnTxHash === activeTransfer.burnTxHash) return null
            if (activeTransfer && tx.id === activeTransfer.id) return null
            const isExpanded = expandedId === tx.id
            const isClaim = tx.type === 'claim'
            const isDismissable = !isClaim && ['mint_success', 'recovered', 'complete', 'failed', 'error'].includes(tx.status)

            return (
              <div key={tx.id} style={{
                background: c.blB, border: `1px solid ${c.dv}`,
                borderRadius: 14,
              }}>
                {/* Summary row — uses div instead of button to avoid Safari/Tailwind preflight rendering issues */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    // Bridge rows → open the full progress view (1-click "see where it's at"). Claims (or when
                    // no handler is wired) fall back to the inline expand. The chevron below still toggles expand.
                    if (!isClaim && onSelectTransfer) { onSelectTransfer(tx); return }
                    setExpandedId(isExpanded ? null : tx.id)
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!isClaim && onSelectTransfer) { onSelectTransfer(tx) } else { setExpandedId(isExpanded ? null : tx.id) } } }}
                  style={rowStyle}
                >
                  {/* Asset visual — from→to hop for bridges (#261 L6, see hopMarks), and for a
                      CLAIM the chain badge it has always been. A claim has one leg and its row
                      already prints "+{amount} {token}" alongside, so a token mark here would
                      restate the text while the chain badge says something the row does not. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    {isClaim ? (
                      <ChainIcon chain={tx.sourceChain} size={18} />
                    ) : (() => {
                      const [from, to] = hopMarks(
                        tx.sourceToken, tx.sourceChain, tx.destToken, tx.destinationChain,
                      )
                      return (
                        <>
                          <LegIcon mark={from} size={18} />
                          <span style={{ fontSize: 10, color: c.mu }}>→</span>
                          <LegIcon mark={to} size={18} />
                        </>
                      )
                    })()}
                  </div>

                  <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: c.tx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {isClaim ? `+${tx.amount} ${tx.sourceToken}` : (
                        <>
                          {tx.amount} {tx.sourceToken}
                          {tx.sourceToken !== tx.destToken && ` → ${tx.destToken}`}
                        </>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: c.mu, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {isClaim && <span style={{ marginRight: 4 }}>Reward claim</span>}
                      {tx.timestamp ? new Date(tx.timestamp).toLocaleString() : ''}
                    </div>
                  </div>

                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 11, padding: '2px 8px', borderRadius: 6,
                    background: statusBg(tx.status), color: statusColor(tx.status),
                    fontWeight: 600, whiteSpace: 'nowrap',
                  }}>
                    <StatusIcon status={tx.status} />
                    {statusLabel(tx.status, tx.type)}
                  </span>

                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                    onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : tx.id) }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); setExpandedId(isExpanded ? null : tx.id) } }}
                    style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', padding: 2 }}
                  >
                    {isExpanded ? <ChevronDown size={14} color={c.mu} /> : <ChevronRight size={14} color={c.mu} />}
                  </span>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div style={{
                    padding: '0 14px 12px',
                    display: 'flex', flexDirection: 'column', gap: 6,
                    animation: 'fadeIn 0.15s ease-out',
                  }}>
                    {renderLegs(tx)}
                    {/* Cancel (left) + Dismiss (right) on one row, right-aligned. Cancel is discoverable for
                        every deBridge cross-chain order, enabled only when cancellable (owned + 'Created'). */}
                    {(tx.cancelControl?.visible || isDismissable) && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        <CancelControlButton tx={tx} />
                        {isDismissable && (
                          <button
                            onClick={() => onDismiss(tx.id)}
                            style={{
                              padding: '4px 10px', fontSize: 11,
                              background: 'transparent', border: `1px solid ${c.dv}`,
                              color: c.mu, borderRadius: 6, cursor: 'pointer',
                              fontFamily: FONT_BODY,
                              WebkitAppearance: 'none', appearance: 'none' as any,
                            }}
                          >
                            Dismiss
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}
